// Boolean path operations (union / subtract / intersect / exclude) and flattening.
// Curves are flattened at high resolution; the result is an editable VECTOR layer.
import polygonClipping, { type MultiPolygon, type Polygon, type Ring } from 'polygon-clipping';
import type { Editor } from './editor.ts';
import type { NodeData, NodeId } from './types.ts';
import { nodeOutline, transformPath, flattenPath, invert, apply, type Mat } from './geometry.ts';
import { makeNode } from './defaults.ts';
import { newId, indexBetween } from './util.ts';
import type { Document } from './document.ts';

/** Index key directly in front of `n` among its siblings.  */
function indexAbove(doc: Document, n: NodeData): string {
  const sib = doc.children(n.parent!);
  const i = sib.findIndex((s) => s.id === n.id);
  return indexBetween(n.index, i + 1 < sib.length ? sib[i + 1].index : null);
}

export type BooleanKind = 'UNION' | 'SUBTRACT' | 'INTERSECT' | 'EXCLUDE';

function ringsOf(n: NodeData, m: Mat): Ring[] {
  const outline = nodeOutline(n);
  if (!outline) return [];
  const polys = flattenPath(transformPath(outline, m), 0.1);
  return polys.filter((p) => p.points.length >= 3).map((p) => {
    const r: Ring = p.points.map((q) => [q.x, q.y]);
    r.push([p.points[0].x, p.points[0].y]);
    return r;
  });
}

/** A node's filled area in page coordinates, respecting its winding rule. */
export function shapeOf(editor: Editor, id: NodeId): MultiPolygon {
  const doc = editor.doc;
  const n = doc.must(id);
  const out: MultiPolygon = [];
  if (n.type === 'GROUP' || n.type === 'FRAME' || n.type === 'COMPONENT' || n.type === 'INSTANCE') {
    // containers contribute the union of their visible leaves
    const parts = doc.children(id).filter((c) => c.visible).map((c) => shapeOf(editor, c.id)).filter((p) => p.length);
    return parts.length ? polygonClipping.union(parts[0], ...parts.slice(1)) : [];
  }
  const rings = ringsOf(n, doc.worldTransform(id));
  if (!rings.length) return out;
  const asPolys: Polygon[] = rings.map((r) => [r]);
  if (asPolys.length === 1) return [asPolys[0]];
  return n.windingRule === 'EVENODD' ? polygonClipping.xor(asPolys[0], ...asPolys.slice(1)) : polygonClipping.union(asPolys[0], ...asPolys.slice(1));
}

function toPath(mp: MultiPolygon, ox: number, oy: number): string {
  const f = (v: number) => +v.toFixed(2);
  let d = '';
  for (const poly of mp) {
    for (const ring of poly) {
      ring.slice(0, -1).forEach(([x, y], i) => {
        d += `${i ? 'L' : 'M'}${f(x - ox)} ${f(y - oy)}`;
      });
      d += 'Z';
    }
  }
  return d;
}

function bboxOf(mp: MultiPolygon) {
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const poly of mp) for (const ring of poly) for (const [x, y] of ring) {
    x0 = Math.min(x0, x);
    y0 = Math.min(y0, y);
    x1 = Math.max(x1, x);
    y1 = Math.max(y1, y);
  }
  return { x0, y0, x1, y1 };
}

/** Replace nodes with a single VECTOR that is their boolean combination. Returns the new node id. */
export function booleanOperation(editor: Editor, ids: NodeId[], kind: BooleanKind): NodeId | null {
  const doc = editor.doc;
  const nodes = editor.topLevel(ids).filter((n) => n.type !== 'PAGE');
  if (nodes.length < (kind === 'UNION' ? 1 : 2)) return null;
  // z-order: bottom-most first (it is the base for subtract and provides the style)
  const ordered = [...nodes].sort((a, b) => {
    if (a.parent === b.parent) return a.index < b.index ? -1 : 1;
    return 0;
  });
  const shapes = ordered.map((n) => shapeOf(editor, n.id)).filter((s) => s.length);
  if (!shapes.length) return null;
  let result: MultiPolygon;
  const [first, ...rest] = shapes;
  switch (kind) {
    case 'UNION':
      result = rest.length ? polygonClipping.union(first, ...rest) : first;
      break;
    case 'SUBTRACT':
      result = polygonClipping.difference(first, ...rest);
      break;
    case 'INTERSECT':
      result = polygonClipping.intersection(first, ...rest);
      break;
    case 'EXCLUDE':
      result = polygonClipping.xor(first, ...rest);
      break;
  }
  const base = ordered[0];
  const top = ordered[ordered.length - 1];
  const parent = base.parent!;
  const b = bboxOf(result);
  if (!Number.isFinite(b.x0)) {
    editor.remove(ordered.map((n) => n.id));
    return null;
  }
  const pw = invert(doc.worldTransform(parent));
  const origin = apply(pw, { x: b.x0, y: b.y0 });
  const id = newId();
  const names: Record<BooleanKind, string> = { UNION: 'Union', SUBTRACT: 'Subtract', INTERSECT: 'Intersect', EXCLUDE: 'Exclude' };
  editor.transact(names[kind], () => {
    editor.applyOps([
      {
        op: 'create',
        node: makeNode('VECTOR', {
          id,
          name: names[kind],
          parent,
          index: top.parent === parent ? indexAbove(doc, top) : doc.indexAt(parent),
          x: origin.x,
          y: origin.y,
          width: Math.max(0.01, b.x1 - b.x0),
          height: Math.max(0.01, b.y1 - b.y0),
          path: toPath(result, b.x0, b.y0),
          windingRule: 'EVENODD',
          fills: structuredClone(base.fills.length ? base.fills : [{ type: 'SOLID', color: { r: 0.85, g: 0.85, b: 0.85, a: 1 } }]),
          strokes: structuredClone(base.strokes),
          strokeWeight: base.strokeWeight,
          strokeAlign: 'CENTER',
          effects: structuredClone(base.effects),
          opacity: base.opacity,
        }),
      },
    ]);
    editor.applyOps(ordered.map((n) => ({ op: 'delete', id: n.id })));
    editor.setSelection([id]);
  });
  return id;
}

/** Convert shapes into editable vector paths that look the same. */
export function flatten(editor: Editor, ids: NodeId[]): NodeId[] {
  const doc = editor.doc;
  const out: NodeId[] = [];
  editor.transact('Flatten', () => {
    for (const n of editor.topLevel(ids)) {
      if (['TEXT', 'CONNECTOR', 'PAGE', 'STICKY', 'SECTION'].includes(n.type)) continue;
      if (n.type === 'GROUP' || n.type === 'FRAME' || n.type === 'COMPONENT' || n.type === 'INSTANCE') {
        const r = booleanOperation(editor, [n.id], 'UNION');
        if (r) out.push(r);
        continue;
      }
      const outline = nodeOutline(n);
      if (!outline) continue;
      // Keep the node's transform (incl. rotation); path stays in local coordinates.
      const d = transformPath(outline, [1, 0, 0, 1, 0, 0]);
      const id = newId();
      editor.applyOps([
        { op: 'create', node: makeNode('VECTOR', { ...structuredClone(n), id, type: 'VECTOR', name: n.name, path: pathOf(d), parent: n.parent, index: indexAbove(doc, n), cornerRadius: 0 }) },
        { op: 'delete', id: n.id },
      ]);
      out.push(id);
    }
    editor.setSelection(out);
  });
  return out;
}

function pathOf(cmds: ReturnType<typeof nodeOutline>): string {
  if (!cmds) return '';
  const f = (v: number) => +v.toFixed(3);
  return cmds
    .map((c) => (c.c === 'Z' ? 'Z' : c.c === 'C' ? `C${f(c.x1)} ${f(c.y1)} ${f(c.x2)} ${f(c.y2)} ${f(c.x)} ${f(c.y)}` : `${c.c}${f(c.x)} ${f(c.y)}`))
    .join('');
}
